/**
 * The unit prices for each currency.
 */
const unitPrices = {
	'EUR': 12,
	'USD': 12.32,
	'GBP': 10.12,
};

/**
 * Calculates the discounted monthly price based on the given price, period, and count.
 *
 * @param {number} basePrice - The original base price.
 * @param {string} currency - The currency for which the price is calculated.
 * @param {string} period - The period for which the price is calculated. Possible values are 'monthly', 'yearly' and 'two-year'.
 * @param {number} count - The additional count.
 * @return {string|number} - The calculated discounted monthly price.
 */
const calculatePrice = ( basePrice, currency, period, count = 1 ) => {

	const unitPrice = unitPrices[ currency ];
	const totalPrice = basePrice + ( unitPrice * ( count - 1 ) );
	let discountedPrice;

	if ( period === 'yearly' ) {

		discountedPrice = totalPrice * 0.9;

	} else if ( period === 'two-year' ) {

		discountedPrice = totalPrice * 0.8;

	} else {

		discountedPrice = totalPrice;

	}

	return discountedPrice % 1 === 0 ? discountedPrice.toFixed( 0 ) : discountedPrice.toFixed( 2 );

};


/**
 * Initializes the dropdown functionality within a specified block.
 * @param {HTMLElement} dropdown - The HTML block containing the dropdown elements.
 * @param {function} callback - Function to be called after a value is selected.
 */
const initDropdown = ( dropdown, callback ) => {

	if ( !dropdown ) {

		return;

	}

	const selected = dropdown.querySelector( '.js-currency-selected' );
	const selectedText = selected.querySelector( '.c-pricing-currency__selected-text' );
	const dropdownMenu = dropdown.querySelector( '.js-currency-dropdown' );
	const buttons = dropdownMenu.querySelectorAll( '.c-pricing-currency__btn' );
	const desktopBP = window.matchMedia( '(min-width: 769px)' ).matches;

	/**
	 * Handles the click event for each button in the dropdown menu.
	 * @param {Event} event - The event object.
	 * @return {void}
	 */
	const handleButtonClick = ( event ) => {

		const value = event.target.textContent;
		selectedText.textContent = value;
		dropdown.classList.remove( 'is-opened' );
		dropdownMenu.classList.add( 'is-disabled' );
		setTimeout( () => {

			dropdownMenu.classList.remove( 'is-disabled' );

		}, 200 );

		if ( callback ) {

			callback( value );

		}

	};

	// Adding event listeners to each button
	buttons.forEach( button => {

		button.addEventListener( 'click', handleButtonClick );

	} );

	if ( desktopBP ) {


		dropdown.addEventListener( 'mouseenter', () => dropdown.classList.add( 'is-opened' ) );
		dropdown.addEventListener( 'mouseleave', () => dropdown.classList.remove( 'is-opened' ) );

		/**
		 * Handles the click event for the document.
		 * @param {Event} event - The event object.
		 * @return {void}
		 */
		const handleDocumentClick = ( event ) => {

			if ( !dropdown.contains( event.target ) ) {

				dropdown.classList.remove( 'is-opened' );

			}

		};

		// Adding event listener to the document
		document.addEventListener( 'click', handleDocumentClick );

	}

};

/**
 * Initializes the pricing count functionality within a specified block.
 * @param {HTMLElement} counter - The HTML block containing the pricing count elements.
 * @param {function} callback - Function to be called after each value update.
 */
const initPricingCount = ( counter, callback ) => {

	const pricingCount = counter;
	if ( !pricingCount ) {

		return;

	}

	const minusButton = pricingCount.querySelector( '.js-pricing-count-minus' );
	const plusButton = pricingCount.querySelector( '.js-pricing-count-plus' );
	const input = pricingCount.querySelector( '.js-pricing-number' );

	/**
	 * Updates the state of the minus button.
	 */
	const updateButtonState = () => {

		const value = parseInt( input.value );
		minusButton.classList.toggle( 'is-disabled', value <= 1 );

	};

	/**
	 * Validates and corrects the value entered in the input field.
	 */
	const validateInput = () => {

		let value = parseInt( input.value );
		if ( isNaN( value ) || value < 1 ) {

			value = 1;

		}


		input.value = value
		updateButtonState();
		if ( callback ) {

			callback( value );

		}

	};

	/**
	 * Handles the click event for the minus button.
	 */
	const handleMinusClick = () => {

		const value = parseInt( input.value );
		if ( value > 1 ) {

			input.value = value - 1;
			updateButtonState();
			if ( callback ) {

				callback( value - 1 );

			}

		}

	};

	/**
	 * Handles the click event for the plus button.
	 */
	const handlePlusClick = () => {

		const value = parseInt( input.value );
		input.value = value + 1;
		updateButtonState();
		if ( callback ) {

			callback( value + 1 );

		}

	};


	// Adding event listeners
	minusButton.addEventListener( 'click', handleMinusClick );
	plusButton.addEventListener( 'click', handlePlusClick );
	input.addEventListener( 'input', validateInput );
	input.addEventListener( 'blur', validateInput );
	input.addEventListener( 'change', validateInput );

	// Initializing the state of the button
	updateButtonState();

};

/**
 * Updates the prices in the pricing block.
 * @param {HTMLElement} block - The pricing block.
 * @return {void}
 */
const updatePrices = ( block ) => {

	const counterVal = parseInt( block.querySelector( '.js-pricing-number' ).value );
	const activePlan = block.querySelector( '.js-pricing-plan-select.is-active' );
	const period = activePlan.dataset.period;
	const prices = block.querySelectorAll( '.b-pricing-box:not(.b-pricing-box--free) .b-pricing-box-price.is-active' );
	const currency = block.querySelector( '.c-pricing-currency__selected-text' ).textContent;

	prices.forEach( ( price ) => {

		const priceVal = parseFloat( price.dataset.price );
		price.querySelector( '.b-pricing-box-price__amount' ).textContent = calculatePrice( priceVal, currency, period, counterVal );

	} );

}

/**
 * Update the mobile header.
 * @param {HTMLElement} mobileWrapper - The mobile wrapper
 */
const updateMobileHeader = ( mobileWrapper ) => {

	const counterVal = parseInt( mobileWrapper.querySelector( '.js-pricing-number' ).value );
	const currency = mobileWrapper.querySelector( '.c-pricing-currency__selected-text' ).textContent;
	const activePlan = mobileWrapper.querySelector( '.js-pricing-plan-select.is-active' );
	const periodLabel = activePlan.querySelector( '.c-pricing-plan__btn-text' ).textContent;

	// Update the header
	mobileWrapper.querySelector( '.js-pricing-mobile-currency' ).textContent = `${currency}`;
	mobileWrapper.querySelector( '.js-pricing-mobile-period' ).textContent = `${periodLabel}`;
	mobileWrapper.querySelector( '.js-pricing-mobile-units' ).textContent = `${counterVal < 10 ? `0${counterVal}` : counterVal}`;

}


/**
 * Marks the mobile wrapper as changed and shows the check label if it is opened.
 *
 * @param {HTMLElement} mobileWrapper - The mobile wrapper element.
 */
const contentChanged = ( mobileWrapper ) => {

	const checkLabel = mobileWrapper.querySelector( '.js-pricing-mobile-check-label' );

	if ( mobileWrapper.classList.contains( 'is-opened' ) ) {

		mobileWrapper.hasChanged = true;
		checkLabel.classList.add( 'is-visible' );

	} else {

		mobileWrapper.hasChanged = false;
		checkLabel.classList.remove( 'is-visible' );

	}

}

document.querySelectorAll( '.b-pricing' ).forEach( ( block ) => {

	const counters = block.querySelectorAll( '.js-pricing-count' );
	const pricingPlans = block.querySelectorAll( '.js-pricing-plan' );
	const dropdowns = block.querySelectorAll( '.js-pricing-currency' );
	const boxes = block.querySelectorAll( '.b-pricing-box' );
	if ( counters.length <= 0 || pricingPlans.length <= 0 || boxes.length <= 0 || dropdowns.length <= 0 ) {

		return;

	}
	const mobileWrapper = block.querySelector( '.js-pricing-mobile' );
	const mobileOpenBtn = block.querySelector( '.js-pricing-mobile-open' );
	const mobileCloseBtn = block.querySelector( '.js-pricing-mobile-close' );
	const mobileWrapperHeight = mobileWrapper.offsetHeight;


	/**
	 * Initialize the pricing count functionality.
	 */
	counters.forEach( ( counter ) => {

		initPricingCount( counter, ( value ) => {

			// Update all instances
			counters.forEach( ( el ) => {

				const inputCounter = el.querySelector( '.js-pricing-number' );
				if ( inputCounter.value !== value.toString() ) {

					inputCounter.value = value;
					const event = new Event( 'blur' );
					inputCounter.dispatchEvent( event );

				}

			} );
			// Update prices
			updatePrices( block );
			// Update mobile header
			updateMobileHeader( mobileWrapper );
			// Mark the mobile wrapper as changed
			contentChanged( mobileWrapper );

		} );

	} );

	/**
	 * Initialize the dropdown functionality.
	 */
	dropdowns.forEach( ( dropdown ) => {

		initDropdown( dropdown, ( value ) => {

			let currencyValue;
			switch ( value ) {

			case 'USD':
				currencyValue = 'dollar';
				break;
			case 'GBP':
				currencyValue = 'pound';
				break;
			case 'EUR':
			default:
				currencyValue = 'euro';
				break;

			}

			// Update all instances
			dropdowns.forEach( ( el ) => el.querySelector( '.c-pricing-currency__selected-text' ).textContent = value );

			// Update prices
			const prices = block.querySelectorAll( '.b-pricing-box:not(.b-pricing-box--free) .b-pricing-box-price' );
			prices.forEach( ( price ) => price.classList.remove( 'is-active' ) );
			const selectedPrices = block.querySelectorAll( `.b-pricing-box:not(.b-pricing-box--free) .b-pricing-box-price--${currencyValue}` );

			selectedPrices.forEach( ( price ) => price.classList.add( 'is-active' ) );
			updatePrices( block );

			// Update mobile header
			updateMobileHeader( mobileWrapper );
			// Mark the mobile wrapper as changed
			contentChanged( mobileWrapper );

		} );

	} );


	/**
	 * Update pricing plans.
	 */
	pricingPlans.forEach( ( pricingPlan ) => {

		const planButtons = pricingPlan.querySelectorAll( '.js-pricing-plan-select' );
		planButtons.forEach( ( planButton, index ) => {

			planButton.classList.add( `js-pricing-plan-select-${index}` )
			planButton.addEventListener( 'click', () => {

				const activePlan = block.querySelectorAll( '.js-pricing-plan-select.is-active' );
				activePlan.forEach( ( el ) => el.classList.remove( 'is-active' ) );
				const allMatchingItems = document.querySelectorAll( `.js-pricing-plan-select-${index}` );
				allMatchingItems.forEach( ( el ) => el.classList.add( 'is-active' ) );

				// Update prices
				updatePrices( block );
				// Update mobile header
				updateMobileHeader( mobileWrapper );
				// Mark the mobile wrapper as changed
				contentChanged( mobileWrapper );

			} );

		} );

	} );


	/**
	 * Open mobile pricing.
	 */
	mobileOpenBtn.addEventListener( 'click', () => {

		mobileWrapper.classList.add( 'is-opened' );
		mobileWrapper.hasChanged = false;

	} );

	/**
	 * Close mobile pricing.
	 */
	mobileCloseBtn.addEventListener( 'click', () => {

		mobileWrapper.classList.remove( 'is-opened' );
		// Mark the mobile wrapper as changed
		contentChanged( mobileWrapper );

	} );

	/**
	 * Update mobile header.
	 * @param {HTMLElement} mobileWrapper - The mobile wrapper
	 */
	document.addEventListener( 'scroll', () => {

		const pricingInner = block.querySelector( '.b-pricing-inner' );
		const mobileEl = block.querySelector( '.js-pricing-mobile' );

		if ( !pricingInner || !mobileEl ) {

			return;

		}
		const pricingInnerRect = pricingInner.getBoundingClientRect();
		const isVisible = pricingInnerRect.top <= mobileWrapperHeight && pricingInnerRect.bottom > 0;

		if ( isVisible ) {

			mobileEl.classList.add( 'is-visible' );

		} else {

			mobileEl.classList.remove( 'is-visible' );
			mobileEl.classList.remove( 'is-opened' );

		}

	} );

} );
